import React, { Suspense, useRef, useState, useEffect } from 'react';
import { Canvas, useThree, useLoader, useFrame } from '@react-three/fiber';
import { 
  OrbitControls, 
  Environment, 
  ContactShadows, 
  TransformControls,
  Text,
  Plane,
  Box
} from '@react-three/drei';
import * as THREE from 'three';
import { 
  CubeIcon, 
  MapIcon, 
  ViewColumnsIcon
} from '@heroicons/react/24/outline';

// Dynamic Front Wall Component with camera-based visibility
const FrontWall = ({ width, height, depth }) => {
  const { camera } = useThree();
  const meshRef = useRef();
  const [isVisible, setIsVisible] = useState(false);

  useFrame(() => {
    if (meshRef.current && camera) {
      // Get camera position
      const cameraZ = camera.position.z;
      const frontWallZ = depth / 2;
      
      // Show wall if camera is behind it (looking from outside)
      // Hide wall if camera is in front (inside view)
      const shouldShow = cameraZ < frontWallZ - 0.5; // Small buffer to prevent flickering
      
      if (shouldShow !== isVisible) {
        setIsVisible(shouldShow);
      }
      
      // Apply visibility with smooth transition
      if (meshRef.current.material) {
        const targetOpacity = shouldShow ? 1 : 0;
        const currentOpacity = meshRef.current.material.opacity;
        const newOpacity = THREE.MathUtils.lerp(currentOpacity, targetOpacity, 0.1);
        
        meshRef.current.material.opacity = newOpacity;
        meshRef.current.material.transparent = true;
        meshRef.current.visible = newOpacity > 0.01;
      }
    }
  });

  return (
    <Plane 
      ref={meshRef}
      args={[width, height]} 
      position={[0, height / 2, depth / 2]}
      rotation={[0, Math.PI, 0]}
      receiveShadow
    >
      <meshStandardMaterial 
        color="#fafafa" 
        roughness={0.9}
        metalness={0.0}
        transparent={true}
        opacity={0}
      />
    </Plane>
  );
};

// 3D Door Component
const Door3D = ({ position, rotation, width = 0.8, height = 2.0 }) => {
  return (
    <group position={position} rotation={rotation}>
      {/* Door frame */}
      <Box args={[width + 0.1, height + 0.1, 0.1]} position={[0, height/2, 0]}>
        <meshStandardMaterial color="#8B4513" roughness={0.8} />
      </Box>
      
      {/* Door panel */}
      <Box args={[width, height, 0.05]} position={[width/4, height/2, 0.02]}>
        <meshStandardMaterial color="#D2691E" roughness={0.6} />
      </Box>
      
      {/* Door handle */}
      <Box args={[0.03, 0.15, 0.08]} position={[width * 0.4, height * 0.5, 0.05]}>
        <meshStandardMaterial color="#FFD700" metalness={0.8} roughness={0.2} />
      </Box>
    </group>
  );
};

// 3D Window Component - More realistic design
const Window3D = ({ position, rotation, width = 1.2, height = 1.0 }) => {
  return (
    <group position={position} rotation={rotation}>
      {/* Window frame - outer frame */}
      <Box args={[width + 0.15, height + 0.15, 0.2]} position={[0, height/2 + 1, -0.05]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.4} />
      </Box>
      
      {/* Window recess - creates depth */}
      <Box args={[width + 0.05, height + 0.05, 0.12]} position={[0, height/2 + 1, -0.02]}>
        <meshStandardMaterial color="#4A4A4A" roughness={0.6} />
      </Box>
      
      {/* Window glass - main glass panel */}
      <Box args={[width - 0.05, height - 0.05, 0.01]} position={[0, height/2 + 1, 0.04]}>
        <meshStandardMaterial 
          color="#E6F3FF" 
          transparent={true} 
          opacity={0.15}
          roughness={0.05}
          metalness={0.0}
          envMapIntensity={1.2}
        />
      </Box>
      
      {/* Window mullions - vertical center divider */}
      <Box args={[0.03, height - 0.05, 0.02]} position={[0, height/2 + 1, 0.05]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      
      {/* Window mullions - horizontal center divider */}
      <Box args={[width - 0.05, 0.03, 0.02]} position={[0, height/2 + 1, 0.05]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      
      {/* Window sill - bottom ledge */}
      <Box args={[width + 0.2, 0.1, 0.25]} position={[0, 1 - 0.05, -0.1]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.7} />
      </Box>
      
      {/* Inner window frames - 4 glass panels */}
      {/* Top left panel frame */}
      <Box args={[0.02, height/2 - 0.04, 0.015]} position={[-width/4, height/2 + 1 + height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      <Box args={[width/2 - 0.04, 0.02, 0.015]} position={[-width/4, height/2 + 1 + height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      
      {/* Top right panel frame */}
      <Box args={[0.02, height/2 - 0.04, 0.015]} position={[width/4, height/2 + 1 + height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      <Box args={[width/2 - 0.04, 0.02, 0.015]} position={[width/4, height/2 + 1 + height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      
      {/* Bottom left panel frame */}
      <Box args={[0.02, height/2 - 0.04, 0.015]} position={[-width/4, height/2 + 1 - height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      <Box args={[width/2 - 0.04, 0.02, 0.015]} position={[-width/4, height/2 + 1 - height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      
      {/* Bottom right panel frame */}
      <Box args={[0.02, height/2 - 0.04, 0.015]} position={[width/4, height/2 + 1 - height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
      <Box args={[width/2 - 0.04, 0.02, 0.015]} position={[width/4, height/2 + 1 - height/4, 0.055]}>
        <meshStandardMaterial color="#3A3A3A" roughness={0.3} />
      </Box>
    </group>
  );
};

// Dynamic room environment with floor texture and doors/windows
const RealisticRoom = ({ width = 5, depth = 4, height = 2.7, floorPlan = null }) => {
  // Load wood floor texture
  const floorTexture = useLoader(THREE.TextureLoader, '/images/WoodFloor01.jpg');
  
  // Configure texture properties
  useEffect(() => {
    if (floorTexture) {
      // Set wrapping mode for seamless tiling
      floorTexture.wrapS = THREE.RepeatWrapping;
      floorTexture.wrapT = THREE.RepeatWrapping;
      
      // Calculate texture repeat based on room dimensions
      // Assuming each parquet tile is about 0.5m x 0.5m in real life
      const tileSize = 0.5;
      const repeatX = width / tileSize;
      const repeatY = depth / tileSize;
      
      floorTexture.repeat.set(repeatX, repeatY);
      
      // Set texture filtering for better quality
      floorTexture.minFilter = THREE.LinearMipmapLinearFilter;
      floorTexture.magFilter = THREE.LinearFilter;
      
      // Mark texture as needing update
      floorTexture.needsUpdate = true;
    }
  }, [floorTexture, width, depth]);
  
  return (
    <group>
      {/* Floor with realistic wood texture and reflection */}
      <Plane 
        args={[width, depth]} 
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, 0, 0]}
        receiveShadow
      >
        <meshStandardMaterial 
          map={floorTexture}
          roughness={0.3}  // Daha az pürüzlülük = daha parlak
          metalness={0.05} // Ahşap için düşük metalness
          envMapIntensity={0.4} // Environment reflection yoğunluğu
          transparent={false}
          // Clearcoat efekti için
          clearcoat={0.8}
          clearcoatRoughness={0.1}
        />
      </Plane>
      
      {/* Back Wall */}
      <Plane 
        args={[width, height]} 
        position={[0, height / 2, -depth / 2]}
        receiveShadow
      >
        <meshStandardMaterial 
          color="#ffffff" 
          roughness={0.9}
          metalness={0.0}
        />
      </Plane>
      
      {/* Left Wall */}
      <Plane 
        args={[depth, height]} 
        position={[-width / 2, height / 2, 0]} 
        rotation={[0, Math.PI / 2, 0]}
        receiveShadow
      >
        <meshStandardMaterial 
          color="#fafafa" 
          roughness={0.9}
          metalness={0.0}
        />
      </Plane>
      
      {/* Right Wall */}
      <Plane 
        args={[depth, height]} 
        position={[width / 2, height / 2, 0]} 
        rotation={[0, -Math.PI / 2, 0]}
        receiveShadow
      >
        <meshStandardMaterial 
          color="#fafafa" 
          roughness={0.9}
          metalness={0.0}
        />
      </Plane>
      
      {/* Front Wall - Dynamic visibility based on camera position */}
      <FrontWall width={width} height={height} depth={depth} />
      
      {/* Ceiling */}
      <Plane 
        args={[width, depth]} 
        rotation={[Math.PI / 2, 0, 0]} 
        position={[0, height, 0]}
      >
        <meshStandardMaterial 
          color="#f5f5f5" 
          roughness={0.9}
          metalness={0.0}
        />
      </Plane>
      
      {/* Render doors from floor plan */}
      {floorPlan && floorPlan.doors && floorPlan.doors.map((door, index) => {
        
        // Use actual room dimensions from 2D plan
        const scale = floorPlan.scale || 20; // pixels per meter
        
        // Find the bounding box of all walls to understand the room layout
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        if (floorPlan.walls) {
          floorPlan.walls.forEach(wall => {
            minX = Math.min(minX, wall.start.x, wall.end.x);
            maxX = Math.max(maxX, wall.start.x, wall.end.x);
            minY = Math.min(minY, wall.start.y, wall.end.y);
            maxY = Math.max(maxY, wall.start.y, wall.end.y);
          });
        }
        
        // Calculate center of the room in 2D
        const roomCenterX = (minX + maxX) / 2;
        const roomCenterY = (minY + maxY) / 2;
        
        // Convert door position relative to room center
        const doorCenterX = (door.start.x + door.end.x) / 2;
        const doorCenterY = (door.start.y + door.end.y) / 2;
        
        // Transform to 3D coordinates (room center = origin)
        const worldX = (doorCenterX - roomCenterX) / scale;
        const worldZ = -(doorCenterY - roomCenterY) / scale; // Negative for Y-axis flip
        
        // Calculate door rotation based on which wall it's on
        const dx = door.end.x - door.start.x;
        const dy = door.end.y - door.start.y;
        
        // Determine if door is more horizontal or vertical
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        
        let rotation;
        if (absX > absY) {
          // Horizontal door (on top/bottom wall) - door should face forward/back
          rotation = [0, 0, 0]; 
        } else {
          // Vertical door (on left/right wall) - door should face left/right  
          rotation = [0, Math.PI / 2, 0]; // 90 degrees
        }
        
        
        // Calculate door width
        const doorWidth = Math.sqrt(dx * dx + dy * dy) / scale;
        
        
        return (
          <Door3D
            key={`door-${index}`}
            position={[worldX, 0, worldZ]}
            rotation={rotation}
            width={Math.max(0.6, Math.min(1.2, doorWidth))}
            height={2.0}
          />
        );
      })}
      
      {/* Render windows from floor plan */}
      {floorPlan && floorPlan.windows && floorPlan.windows.map((window, index) => {
        // Use same algorithm as doors
        const scale = floorPlan.scale || 20; // pixels per meter
        
        // Find the bounding box of all walls to understand the room layout
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        if (floorPlan.walls) {
          floorPlan.walls.forEach(wall => {
            minX = Math.min(minX, wall.start.x, wall.end.x);
            maxX = Math.max(maxX, wall.start.x, wall.end.x);
            minY = Math.min(minY, wall.start.y, wall.end.y);
            maxY = Math.max(maxY, wall.start.y, wall.end.y);
          });
        }
        
        // Calculate center of the room in 2D
        const roomCenterX = (minX + maxX) / 2;
        const roomCenterY = (minY + maxY) / 2;
        
        // Convert window position relative to room center
        const windowCenterX = (window.start.x + window.end.x) / 2;
        const windowCenterY = (window.start.y + window.end.y) / 2;
        
        // Transform to 3D coordinates (room center = origin)
        const worldX = (windowCenterX - roomCenterX) / scale;
        const worldZ = -(windowCenterY - roomCenterY) / scale; // Negative for Y-axis flip
        
        // Calculate window rotation based on which wall it's on
        const dx = window.end.x - window.start.x;
        const dy = window.end.y - window.start.y;
        
        // Determine if window is more horizontal or vertical
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        
        let rotation;
        if (absX > absY) {
          // Horizontal window (on top/bottom wall) - window should face forward/back
          rotation = [0, 0, 0]; 
        } else {
          // Vertical window (on left/right wall) - window should face left/right  
          rotation = [0, Math.PI / 2, 0]; // 90 degrees
        }
        
        // Calculate window width
        const windowWidth = Math.sqrt(dx * dx + dy * dy) / scale;
        
        return (
          <Window3D
            key={`window-${index}`}
            position={[worldX, 0, worldZ]}
            rotation={rotation}
            width={Math.max(0.8, Math.min(2.0, windowWidth))}
            height={1.2}
          />
        );
      })}
      
    </group>
  );
};

// Wall collision detection - dynamic room bounds
const checkWallCollision = (position, furnitureSize = 0.5, roomDimensions = { width: 5, depth: 4, height: 2.7 }, isRotating = false) => {
  // Increase buffer zone for better collision detection
  const buffer = furnitureSize + 0.3; // Add extra buffer
  
  const roomBounds = {
    minX: -roomDimensions.width / 2 + buffer,
    maxX: roomDimensions.width / 2 - buffer,
    minZ: -roomDimensions.depth / 2 + buffer,
    maxZ: roomDimensions.depth / 2 - buffer
  };
  
  const clampedPosition = [
    Math.max(roomBounds.minX, Math.min(roomBounds.maxX, position[0])),
    Math.max(0, position[1]), // Keep above floor
    Math.max(roomBounds.minZ, Math.min(roomBounds.maxZ, position[2]))
  ];
  
  return clampedPosition;
};

// Enhanced furniture item with realistic sizing and collision
const RealisticFurnitureItem = ({ 
  furniture, 
  isSelected, 
  onSelect, 
  onUpdate, 
  transformMode,
  orbitControlsRef,
  roomDimensions
}) => {
  const meshRef = useRef();
  const transformControlsRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [processedScene, setProcessedScene] = useState(null);
  const [furnitureSize, setFurnitureSize] = useState(0.5);
  const [isTransforming, setIsTransforming] = useState(false);

  // Process the scene with realistic furniture sizing
  useEffect(() => {
    if (furniture.model && furniture.model.scene) {
      const originalScene = furniture.model.scene;
      const clonedScene = originalScene.clone();
      
      // Calculate bounding box
      const box = new THREE.Box3().setFromObject(clonedScene);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      
      const maxDimension = Math.max(size.x, size.y, size.z);
      
      // Use dimensions from catalog if available, otherwise use intelligent sizing
      let targetSize = 1;
      
      if (furniture.originalData && furniture.originalData.dimensions) {
        // Use the largest dimension from catalog (convert cm to meters)
        const dims = furniture.originalData.dimensions;
        targetSize = Math.max(dims.width, dims.height, dims.depth) / 100; // Convert cm to meters
      } else {
        // Fallback to name-based sizing
        const name = furniture.name.toLowerCase();
        if (name.includes('chair') || name.includes('sandalye')) {
          targetSize = 0.6;
        } else if (name.includes('table') || name.includes('masa')) {
          targetSize = 1.2;
        } else if (name.includes('sofa') || name.includes('koltuk')) {
          targetSize = 2.0;
        } else if (name.includes('bed') || name.includes('yatak')) {
          targetSize = 2.0;
        } else if (name.includes('wardrobe') || name.includes('dolap')) {
          targetSize = 1.8;
        } else {
          targetSize = 1.0;
        }
      }
      
      const scaleFactor = targetSize / maxDimension;
      
      // Apply scaling
      clonedScene.scale.setScalar(scaleFactor);
      
      // Center and position on floor
      clonedScene.position.set(0, 0, 0);
      clonedScene.position.sub(center.multiplyScalar(scaleFactor));
      
      const scaledBox = new THREE.Box3().setFromObject(clonedScene);
      const scaledSize = scaledBox.getSize(new THREE.Vector3());
      clonedScene.position.y = scaledSize.y / 2;
      
      // Calculate actual furniture size for collision (use the largest horizontal dimension)
      const furnitureCollisionSize = Math.max(scaledSize.x, scaledSize.z) / 2;
      setFurnitureSize(furnitureCollisionSize);
      
      // Enhance materials for better visuals
      clonedScene.traverse((child) => {
        if (child.isMesh) {
          if (child.material) {
            // Clone material to avoid shared references
            child.material = child.material.clone();
            child.material.transparent = false;
            child.material.opacity = 1;
            child.material.visible = true;
            
            // Add realistic material properties
            if (!child.material.color || child.material.color.getHex() === 0x000000) {
              child.material.color = new THREE.Color(0xa0a0a0);
            }
            
            // Set realistic material properties
            child.material.roughness = child.material.roughness || 0.7;
            child.material.metalness = child.material.metalness || 0.1;
          }
          child.visible = true;
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      
      
      setProcessedScene(clonedScene);
    }
  }, [furniture.model, furniture.name, furniture.originalData]);

  // Apply textures
  useEffect(() => {
    if (furniture.texture && processedScene) {
      processedScene.traverse((child) => {
        if (child.isMesh && child.material) {
          // Apply texture properties
          child.material.color = new THREE.Color(furniture.texture.color);
          
          switch (furniture.texture.pattern) {
            case 'leather':
              child.material.roughness = 0.6;
              child.material.metalness = 0.1;
              break;
            case 'fabric':
              child.material.roughness = 0.9;
              child.material.metalness = 0.0;
              break;
            case 'velvet':
              child.material.roughness = 0.95;
              child.material.metalness = 0.0;
              break;
            case 'wood':
              child.material.roughness = 0.7;
              child.material.metalness = 0.0;
              break;
            default:
              break;
          }
        }
      });
    }
  }, [furniture.texture, processedScene]);

  // Handle transform with wall collision
  const handleTransform = () => {
    if (meshRef.current) {
      let position = meshRef.current.position.toArray();
      
      // Apply wall collision detection for both translate and rotate modes
      position = checkWallCollision(position, furnitureSize, roomDimensions, transformMode === 'rotate');
      meshRef.current.position.fromArray(position);
      
      const rotation = meshRef.current.rotation.toArray().slice(0, 3);
      const scale = meshRef.current.scale.toArray();
      
      onUpdate(furniture.id, { position, rotation, scale });
    }
  };

  // Handle transform start - disable orbit controls
  const handleTransformStart = () => {
    setIsTransforming(true);
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = false;
    }
  };

  // Handle transform end - enable orbit controls
  const handleTransformEnd = () => {
    setIsTransforming(false);
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = true;
    }
    handleTransform();
  };

  if (!processedScene) {
    return null;
  }

  return (
    <group>
      <primitive
        ref={meshRef}
        object={processedScene}
        position={furniture.position}
        rotation={furniture.rotation}
        scale={furniture.scale}
        onClick={(e) => {
          e.stopPropagation();
          if (!isTransforming) {
            onSelect(furniture);
          }
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          if (!isTransforming) {
            setHovered(true);
            document.body.style.cursor = 'pointer';
          }
        }}
        onPointerOut={(e) => {
          setHovered(false);
          document.body.style.cursor = 'auto';
        }}
      />
      
      {/* Subtle selection highlight */}
      {(isSelected || hovered) && !isTransforming && (
        <Box
          args={[furnitureSize * 2.2, 0.05, furnitureSize * 2.2]}
          position={[furniture.position[0], 0.01, furniture.position[2]]}
        >
          <meshBasicMaterial
            color={isSelected ? "#60a5fa" : "#ffffff"}
            transparent
            opacity={isSelected ? 0.3 : 0.1}
          />
        </Box>
      )}
      
      {/* Enhanced transform controls */}
      {isSelected && meshRef.current && (
        <TransformControls
          ref={transformControlsRef}
          object={meshRef.current}
          mode={transformMode}
          onObjectChange={handleTransform}
          onMouseDown={handleTransformStart}
          onMouseUp={handleTransformEnd}
          // Show controls based on mode
          showX={transformMode === 'translate'}
          showY={transformMode === 'rotate'} // Show Y rotation in rotate mode
          showZ={transformMode === 'translate'}
          // Hide unwanted controls
          showXY={false}
          showXZ={false}
          showYZ={false}
          size={0.8}
          space="world"
          translationSnap={0.1}
          rotationSnap={Math.PI / 8} // 22.5 degree snapping
        />
      )}
    </group>
  );
};

// Enhanced scene content
const EnhancedSceneContent = ({ 
  furnitureModels, 
  selectedFurniture, 
  onFurnitureSelect, 
  onFurnitureUpdate,
  roomDimensions,
  floorPlan,
  transformMode,
  setTransformMode,
  cameraMode,
  setCameraMode
}) => {
  const { camera } = useThree();
  const orbitControlsRef = useRef();
  
  // Calculate 3D camera position from 2D floor plan
  const get3DCameraFromPlan = () => {
    if (!floorPlan || !floorPlan.camera) {
      return null;
    }
    
    const cameraData = floorPlan.camera;
    const scale = floorPlan.scale || 40;
    
    // Find room bounds
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    if (floorPlan.walls && floorPlan.walls.length > 0) {
      floorPlan.walls.forEach(wall => {
        minX = Math.min(minX, wall.start.x, wall.end.x);
        maxX = Math.max(maxX, wall.start.x, wall.end.x);
        minY = Math.min(minY, wall.start.y, wall.end.y);
        maxY = Math.max(maxY, wall.start.y, wall.end.y);
      });
      
      // Calculate room center
      const roomCenterX = (minX + maxX) / 2;
      const roomCenterY = (minY + maxY) / 2;
      
      // Convert camera position to 3D world coordinates
      const worldX = (cameraData.position.x - roomCenterX) / scale;
      const worldZ = -(cameraData.position.y - roomCenterY) / scale;
      
      // Camera height (eye level) with vertical angle adjustment
      const baseHeight = 1.7;
      const worldY = baseHeight;
      
      // Calculate look direction from horizontal angle
      const lookDistance = 2;
      const lookX = worldX + Math.cos(cameraData.angle) * lookDistance;
      const lookZ = worldZ - Math.sin(cameraData.angle) * lookDistance; // Negative for Y-axis flip
      const lookY = baseHeight + Math.sin(cameraData.verticalAngle) * lookDistance;
      
      return {
        position: [worldX, worldY, worldZ],
        target: [lookX, lookY, lookZ],
        verticalAngle: cameraData.verticalAngle
      };
    }
    
    return null;
  };

  // Camera and furniture controls
  useEffect(() => {
    const handleKeyPress = (event) => {
      switch (event.key.toLowerCase()) {
        case 'c':
          // Toggle camera mode
          const cameraData = get3DCameraFromPlan();
          if (cameraData) {
            setCameraMode(cameraMode === 'free' ? 'fixed' : 'free');
          }
          break;
        case 'r':
          if (selectedFurniture) {
            setTransformMode(transformMode === 'rotate' ? 'translate' : 'rotate');
          }
          break;
        default:
          break;
      }
    };

    const handleArrowKeys = (event) => {
      // Only allow camera movement in fixed camera mode
      if (cameraMode !== 'fixed') return;
      
      event.preventDefault();
      const moveSpeed = 0.1;
      const rotateSpeed = 0.05;
      
      switch (event.key) {
        case 'ArrowUp':
          // Move forward
          camera.position.z -= moveSpeed * Math.cos(camera.rotation.y);
          camera.position.x -= moveSpeed * Math.sin(camera.rotation.y);
          break;
        case 'ArrowDown':
          // Move backward
          camera.position.z += moveSpeed * Math.cos(camera.rotation.y);
          camera.position.x += moveSpeed * Math.sin(camera.rotation.y);
          break;
        case 'ArrowLeft':
          // Rotate left
          camera.rotation.y += rotateSpeed;
          break;
        case 'ArrowRight':
          // Rotate right
          camera.rotation.y -= rotateSpeed;
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('keydown', handleArrowKeys);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('keydown', handleArrowKeys);
    };
  }, [transformMode, selectedFurniture, cameraMode, get3DCameraFromPlan, camera]);

  // Dynamic camera positioning
  useEffect(() => {
    const cameraData = get3DCameraFromPlan();
    
    if (cameraMode === 'fixed' && cameraData) {
      // Use camera from floor plan
      camera.position.set(...cameraData.position);
      camera.lookAt(...cameraData.target);
      
      // Disable orbit controls in fixed camera mode
      if (orbitControlsRef.current) {
        orbitControlsRef.current.enabled = false;
      }
    } else {
      // Free camera mode - default position
      camera.position.set(4, 3, 4);
      camera.lookAt(0, 1, 0);
      
      // Enable orbit controls in free camera mode
      if (orbitControlsRef.current) {
        orbitControlsRef.current.enabled = true;
      }
    }
  }, [camera, cameraMode, floorPlan]);

  return (
    <>
      {/* Enhanced lighting setup */}
      <ambientLight intensity={0.4} color="#f0f0f0" />
      
      {/* Main sunlight from window */}
      <directionalLight
        position={[3, 4, 2]}
        castShadow
        shadow-mapSize-width={4096}
        shadow-mapSize-height={4096}
        shadow-camera-far={20}
        shadow-camera-left={-5}
        shadow-camera-right={5}
        shadow-camera-top={5}
        shadow-camera-bottom={-5}
        intensity={0.8}
        color="#ffffff"
      />
      
      {/* Ceiling light */}
      <pointLight 
        position={[0, 2.5, 0]} 
        intensity={0.3} 
        color="#fffef7"
        castShadow
      />
      
      {/* Corner fill light */}
      <pointLight 
        position={[-2, 2, -1]} 
        intensity={0.2} 
        color="#f0f8ff"
      />

      {/* Realistic environment */}
      <Environment preset="apartment" />
      
      {/* Room */}
      <RealisticRoom 
        width={roomDimensions.width} 
        depth={roomDimensions.depth} 
        height={roomDimensions.height}
        floorPlan={floorPlan}
      />
      
      {/* Soft contact shadows */}
      <ContactShadows
        position={[0, 0.01, 0]}
        opacity={0.5}
        scale={Math.max(roomDimensions.width, roomDimensions.depth) + 2}
        blur={2}
        far={5}
        resolution={512}
        color="#000000"
      />

      {/* Furniture items */}
      {furnitureModels.map((furniture) => (
        <RealisticFurnitureItem
          key={furniture.id}
          furniture={furniture}
          isSelected={selectedFurniture && selectedFurniture.id === furniture.id}
          onSelect={onFurnitureSelect}
          onUpdate={onFurnitureUpdate}
          transformMode={transformMode}
          orbitControlsRef={orbitControlsRef}
          roomDimensions={roomDimensions}
        />
      ))}



      {/* Enhanced camera controls */}
      <OrbitControls
        ref={orbitControlsRef}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={2}
        maxDistance={8}
        minPolarAngle={Math.PI / 6}
        maxPolarAngle={Math.PI / 2.2}
        target={[0, 1, 0]}
        enableDamping={true}
        dampingFactor={0.05}
      />
    </>
  );
};

// Loading component
const LoadingSpinner = () => (
  <div style={{
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#60a5fa',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(255, 255, 255, 0.9)',
    padding: '1rem 2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  }}>
    <div className="spinner"></div>
    3D odanız yükleniyor...
  </div>
);

// Main SceneRenderer component
const SceneRenderer = ({ 
  furnitureModels, 
  selectedFurniture, 
  onFurnitureSelect, 
  onFurnitureUpdate,
  roomDimensions: externalRoomDimensions = null,
  floorPlan = null,
  viewMode = '3d',
  setViewMode = () => {}
}) => {
  // Room dimensions state (use external if provided, otherwise internal state)
  const [internalRoomDimensions, setInternalRoomDimensions] = useState({
    width: 5,
    depth: 4,
    height: 2.7
  });
  
  // Control states
  const [transformMode, setTransformMode] = useState('translate');
  const [cameraMode, setCameraMode] = useState('free'); // 'free' or 'fixed'
  
  const roomDimensions = externalRoomDimensions || internalRoomDimensions;
  const setRoomDimensions = externalRoomDimensions ? () => {} : setInternalRoomDimensions;

  return (
    <div 
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
      onWheel={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Canvas
        shadows={{
          enabled: true,
          type: THREE.PCFSoftShadowMap
        }}
        camera={{ position: [4, 3, 4], fov: 50 }}
        style={{ 
          background: 'linear-gradient(to bottom, #e6f3ff 0%, #cce7ff 50%, #b3dbff 100%)'
        }}
        onPointerMissed={() => {
          onFurnitureSelect(null);
        }}
      >
        <Suspense fallback={null}>
          <EnhancedSceneContent
            furnitureModels={furnitureModels}
            selectedFurniture={selectedFurniture}
            onFurnitureSelect={onFurnitureSelect}
            onFurnitureUpdate={onFurnitureUpdate}
            roomDimensions={roomDimensions}
            floorPlan={floorPlan}
            transformMode={transformMode}
            setTransformMode={setTransformMode}
            cameraMode={cameraMode}
            setCameraMode={setCameraMode}
          />
        </Suspense>
      </Canvas>
      
      <Suspense fallback={<LoadingSpinner />}>
        <div></div>
      </Suspense>

      {/* Floating View Mode Panel */}
      <div style={{
        position: 'absolute',
        top: '1rem',
        left: '1rem',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '1rem',
        padding: '0.5rem',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        zIndex: 1000
      }}>
        <div style={{
          display: 'flex',
          backgroundColor: '#f3f4f6',
          borderRadius: '0.5rem',
          padding: '0.25rem'
        }}>
          <button
            onClick={() => setViewMode('2d')}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: viewMode === '2d' ? '#ffffff' : 'transparent',
              color: viewMode === '2d' ? '#2563eb' : '#6b7280',
              boxShadow: viewMode === '2d' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <MapIcon style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
            2D Plan
          </button>
          <button
            onClick={() => setViewMode('3d')}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: viewMode === '3d' ? '#ffffff' : 'transparent',
              color: viewMode === '3d' ? '#2563eb' : '#6b7280',
              boxShadow: viewMode === '3d' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <CubeIcon style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
            3D Görünüm
          </button>
          <button
            onClick={() => setViewMode('split')}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: viewMode === 'split' ? '#ffffff' : 'transparent',
              color: viewMode === 'split' ? '#2563eb' : '#6b7280',
              boxShadow: viewMode === 'split' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <ViewColumnsIcon style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
            Karma Görünüm
          </button>
        </div>
      </div>

      {/* Room info panel */}
      <div style={{
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        background: 'rgba(255, 255, 255, 0.95)',
        color: '#333',
        padding: '1rem',
        borderRadius: '1rem',
        fontSize: '0.8rem',
        minWidth: '280px',
        maxWidth: '320px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        backdropFilter: 'blur(10px)'
      }}>
        {/* Oda Bilgileri */}
        <div style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: '#2563eb' }}>
          📐 Oda Bilgileri
        </div>
        <div style={{ lineHeight: 1.4, marginBottom: '1rem' }}>
          <div>Boyut: {roomDimensions.width}m × {roomDimensions.depth}m ({Math.round(roomDimensions.width * roomDimensions.depth)}m²)</div>
          <div>Yükseklik: {roomDimensions.height}m</div>
          <div>Mobilya sayısı: {furnitureModels.length}</div>
        </div>

        {/* Kamera Kontrolleri */}
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#dc2626', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
          📷 Kamera Kontrolleri
        </div>
        <div style={{ lineHeight: 1.4, marginBottom: '1rem', fontSize: '0.75rem' }}>
          {floorPlan && floorPlan.camera ? (
            <>
              <div style={{ marginBottom: '0.5rem', padding: '0.5rem', background: 'rgba(220, 38, 38, 0.1)', borderRadius: '0.5rem' }}>
                <strong>Durum:</strong> {cameraMode === 'free' ? 'Serbest Kamera' : 'Sabit Kamera'}<br/>
                <span style={{ color: '#dc2626' }}>
                  <strong>C tuşu:</strong> {cameraMode === 'free' ? 'Sabit kameraya geç' : 'Serbest kameraya geç'}
                </span>
              </div>
              {cameraMode === 'fixed' && (
                <div style={{ fontSize: '0.7rem', color: '#666' }}>
                  ↑↓ Yukarı/Aşağı • ←→ Sola/Sağa dön
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '0.5rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', color: '#b45309' }}>
              ⚠️ 2D panelden kamera ayarlayın
            </div>
          )}
        </div>

        {/* Mobilya Kontrolleri */}
        {selectedFurniture && (
          <>
            <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#059669', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
              🪑 Mobilya Kontrolleri
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ marginBottom: '0.5rem', padding: '0.5rem', background: 'rgba(5, 150, 105, 0.1)', borderRadius: '0.5rem' }}>
                <strong>Seçili:</strong> {selectedFurniture.name}<br/>
                <span style={{ color: '#059669' }}>
                  <strong>R tuşu:</strong> {transformMode === 'rotate' ? 'Taşı moduna geç' : 'Döndür moduna geç'}
                </span>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#666' }}>
                {transformMode === 'rotate' 
                  ? '🔄 Sarı halkayı sürükleyerek döndürün' 
                  : '🔀 Mavi okları sürükleyerek taşıyın'
                }
              </div>
            </div>
          </>
        )}

        {/* Genel Bilgiler */}
        {!selectedFurniture && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', fontSize: '0.7rem', color: '#666' }}>
            💡 Mobilya seçmek için tıklayın
          </div>
        )}
      </div>

      {/* Room Size Controls - only show if not using external dimensions */}
      {!externalRoomDimensions && (
        <div style={{
          position: 'absolute',
          top: '1rem',
          left: '1rem',
          background: 'rgba(255, 255, 255, 0.95)',
          color: '#333',
          padding: '1rem',
          borderRadius: '1rem',
          fontSize: '0.8rem',
          minWidth: '250px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)'
        }}>
        <div style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: '#2563eb' }}>
          🏗️ Oda Boyutları
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Width Control */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>
              Genişlik: {roomDimensions.width}m
            </label>
            <input
              type="range"
              min="3"
              max="8"
              step="0.5"
              value={roomDimensions.width}
              onChange={(e) => setRoomDimensions(prev => ({ 
                ...prev, 
                width: parseFloat(e.target.value) 
              }))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                background: '#e5e7eb',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Depth Control */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>
              Derinlik: {roomDimensions.depth}m
            </label>
            <input
              type="range"
              min="3"
              max="8"
              step="0.5"
              value={roomDimensions.depth}
              onChange={(e) => setRoomDimensions(prev => ({ 
                ...prev, 
                depth: parseFloat(e.target.value) 
              }))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                background: '#e5e7eb',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Height Control */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>
              Yükseklik: {roomDimensions.height}m
            </label>
            <input
              type="range"
              min="2.2"
              max="3.5"
              step="0.1"
              value={roomDimensions.height}
              onChange={(e) => setRoomDimensions(prev => ({ 
                ...prev, 
                height: parseFloat(e.target.value) 
              }))}
              style={{
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                background: '#e5e7eb',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Quick presets */}
          <div style={{ marginTop: '0.5rem' }}>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.75rem', fontWeight: '500', color: '#666' }}>
              Hızlı Ayarlar:
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setRoomDimensions({ width: 4, depth: 4, height: 2.7 })}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.7rem',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Küçük (16m²)
              </button>
              <button
                onClick={() => setRoomDimensions({ width: 5, depth: 4, height: 2.7 })}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.7rem',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Orta (20m²)
              </button>
              <button
                onClick={() => setRoomDimensions({ width: 6, depth: 5, height: 2.7 })}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.7rem',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Büyük (30m²)
              </button>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default SceneRenderer; 